import { useEffect } from 'react';
import { prefetchCatalogsForRange } from '@app/rangeConfig/prefetchCatalogs';

/**
 * Prefetches resources with intelligent prioritization.
 *
 * Strategy:
 * - Wait for window.onload (all images loaded) before starting any prefetch
 * - This ensures images get full network bandwidth priority
 *
 * @param range - Current product range (pax, besta, kallax, platsa)
 *
 * @example
 * ```typescript
 * const range = getRange();
 * useResourcePrefetching(range);
 * ```
 */
export const useResourcePrefetching = (range: string): void => {
  useEffect(() => {
    let cancelled = false;

    const startPrefetching = () => {
      if (cancelled) return;

      // Prefetch catalogs (lightweight JSON)
      prefetchCatalogsForRange(range);

      // Prefetch landing page sheets
      import('@app/views/LandingPage/ui/LandingPageSheets').catch(() => {
        // Silently ignore preload failures
      });

      // Prefetch App last (it's the largest)
      import('@app/Planner').catch(() => {
        // Silently ignore preload failures
      });
    };

    // Wait for window.onload to ensure all images are loaded first
    if (document.readyState === 'complete') {
      // Page already loaded, start prefetching after a short delay
      const timeoutId = setTimeout(startPrefetching, 100);
      return () => {
        cancelled = true;
        clearTimeout(timeoutId);
      };
    } else {
      // Wait for load event
      window.addEventListener('load', startPrefetching);
      return () => {
        cancelled = true;
        window.removeEventListener('load', startPrefetching);
      };
    }
  }, [range]);
};
